import { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import OnboardingPage from "./pages/OnboardingPage";
import FeedPage from "./pages/FeedPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import ShortlistPage from "./pages/ShortlistPage";
import StarredPage from "./pages/StarredPage";
import CarouselPage from "./pages/CarouselPage";
import PricingPage from "./pages/PricingPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!location.state?.user);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [subscription, setSubscription] = useState(null);

  const fetchSubscription = async () => {
    try {
      const res = await api.get("/billing/status");
      setSubscription(res.data);
    } catch {
      // Non-fatal — subscription state defaults to null
    }
  };

  useEffect(() => {
    // If we have user data from location state (e.g. from AuthCallback), use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setIsLoading(false);
      fetchSubscription();
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/me");
        const { auth_token, user_data, has_completed_onboarding } = response.data;

        // Repopulate localStorage for extension sync if session exists
        if (auth_token && user_data) {
          localStorage.setItem("auth_token", auth_token);
          localStorage.setItem("user_data", JSON.stringify(user_data));
        }

        // Merge user_data with has_completed_onboarding for consistent state
        setUser({ ...user_data, has_completed_onboarding });
        setIsAuthenticated(true);
        await fetchSubscription();
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // Clear session data from localStorage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_data");

      setUser(null);
      setIsAuthenticated(false);
      setSubscription(null);
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, isLoading, logout, api, subscription, setSubscription, fetchSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      // Google returns the token in the hash when using response_type=id_token
      const idTokenMatch = hash.match(/id_token=([^&]+)/);

      if (!idTokenMatch) {
        navigate("/", { replace: true });
        return;
      }

      const idToken = idTokenMatch[1];

      try {
        const response = await api.post("/auth/session", { token: idToken });
        const { auth_token, user_data, has_completed_onboarding } = response.data;

        // Store session data for Chrome Extension sync
        localStorage.setItem("auth_token", auth_token);
        localStorage.setItem("user_data", JSON.stringify(user_data));

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);

        // Merge user_data with has_completed_onboarding for consistent state
        const fullUser = { ...user_data, has_completed_onboarding };

        // Navigate based on onboarding status
        if (has_completed_onboarding) {
          navigate("/feed", { replace: true, state: { user: fullUser } });
        } else {
          navigate("/onboarding", { replace: true, state: { user: fullUser } });
        }

        toast.success(`Welcome, ${user_data.name}!`);
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Authenticating...</div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if onboarding is required but not completed
  if (requireOnboarding && user && !user.has_completed_onboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check for id_token synchronously during render to prevent race conditions
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("id_token=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <FeedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shortlist"
        element={
          <ProtectedRoute>
            <ShortlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/starred"
        element={
          <ProtectedRoute>
            <StarredPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/carousels"
        element={
          <ProtectedRoute>
            <CarouselPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          className: "font-sans",
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
export { api, API };
